<template>
  <div class="details-container">
    <div class="details-header">
      <h2>Organization Details</h2>
    </div>
    <div class="details-content">
      <div class="details-section">
        <h3>Information</h3>
        <div class="details-group two-thirds-width">
          <label>Logo</label>
          <div class="details-value">
            <img v-if="organizationData.logo_url" :src="organizationData.logo_url" alt="Organization logo" style="max-height: 100px;">
            <span v-else>No logo provided</span>
          </div>
        </div>

        <div class="details-group half-width">
          <label>Full Legal Name</label>
          <div class="details-value">{{ organizationData.name }}</div>
        </div>

        <div class="details-group half-width">
          <label>VAT/Registration Number</label>
          <div class="details-value">{{ organizationData.registration }}</div>
        </div>

        <div class="details-group half-width">
          <label>Website URL</label>
          <div class="details-value">
            <a v-if="organizationData.website_url" :href="organizationData.website_url">
              {{ organizationData.website_url }}
            </a>
          </div>
        </div>

        <div class="details-group half-width">
          <label>Phone</label>
          <div class="details-value">{{ organizationData.phone }}</div>
        </div>

        <div class="details-group full-width">
          <label>Address</label>
          <div class="details-value">{{ organizationData.address_line_1 }}</div>
        </div>

        <div class="details-group full-width">
          <div class="details-value">{{ organizationData.address_line_2 }}</div>
        </div>

        <div class="details-group half-width">
          <label>City</label>
          <div class="details-value">{{ organizationData.city }}</div>
        </div>

        <div class="details-group quarter-width">
          <label>State</label>
          <div class="details-value">{{ organizationData.state }}</div>
        </div>

        <div class="details-group quarter-width">
          <label>Postal Code</label>
          <div class="details-value">{{ organizationData.postal_code }}</div>
        </div>

        <div class="details-group half-width">
          <label>Country</label>
          <div class="details-value">{{ organizationData.country }}</div>
        </div>
      </div>

      <div class="details-section">
        <h3>Stats</h3>
        <div class="details-group quarter-width">
          <label>Total Video Files</label>
          <div class="details-value">{{ totalFileCount === null ? '-' : totalFileCount }}</div>
        </div>
        <div class="details-group quarter-width">
          <label>Total Storage</label>
          <div class="details-value">{{ totalStorage === null ? '-' : formatFileSize(totalStorage) }}</div>
        </div>
        <div v-if="isSuperuser()" class="details-group half-width">
          <label>Channels</label>
          <div v-if="channels.length > 0" class="channels-list">
            <template v-for="(channel, index) in channels" :key="channel.id">
              <router-link 
                :to="`/channels/${channel.id}`" 
                :title="`View Channel &quot;${channel.name}&quot;`" 
                class="link-button"
              >
                {{ channel.name }}
              </router-link>
              <br v-if="index < channels.length - 1">
            </template>
          </div>
          <div v-else class="details-value">No channels found</div>
        </div>
      </div>

      <OrganizationMembers :organization-id="organizationId" :read-only="true" />

      <div class="details-actions">
        <router-link 
          :to="`/organizations/${organizationId}/edit`" 
          class="important-button big" 
          v-if="isSuperuser()"
        >
          Edit Organization
        </router-link>
        <button class="cancel-button medium" @click="goBack">Back</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import OrganizationMembers from '@/components/OrganizationMembers.vue';
import { isSuperuser } from '@/scripts/permissions';
import { formatFileSize } from '@/scripts/shared-utils';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const organizationData = ref({});
const channels = ref([]);

const organizationId = computed(() => route.params.organization_id);

// Calculate total file count from all channels
const totalFileCount = computed(() => {
  if (!channels.value || channels.value.length === 0) return null;
  
  let fileCount = 0;
  channels.value.forEach(channel => {
    if (channel.video_files && channel.video_files.length > 0) {
      fileCount += channel.video_files.length;
    }
  });
  
  return fileCount || null;
});

// Calculate total storage from all files in all channels
const totalStorage = computed(() => {
  if (!channels.value || channels.value.length === 0) return null;
  
  let storage = 0;
  channels.value.forEach(channel => {
    if (channel.video_files && channel.video_files.length > 0) {
      channel.video_files.forEach(file => {
        if (file.file_size && !isNaN(file.file_size)) {
          storage += Number(file.file_size);
        }
      });
    }
  });
  
  return storage || null;
});

async function fetchOrganizationData() {
  try {
    const response = await axios.get(`/api/organizations/${organizationId.value}`);
    organizationData.value = response.data;
  } catch (error) {
    console.error('Error fetching organization data:', error);
  }
}

async function fetchChannels() {
  try {
    const response = await axios.get(`/api/organizations/${organizationId.value}/channels`);
    channels.value = response.data;
  } catch (error) {
    console.error('Error fetching organization channels:', error);
  }
}

function goBack() {
  router.back();
}

onMounted(async () => {
  await fetchOrganizationData();
  await fetchChannels();
});
</script>

<style scoped>
@import '@/styles/shared-details.css';
</style>
